<template>
    <mds-label
        :on-dark="onDark"
        :size="size"
        :optional="optional"
        :optional-text="optionalText"
        :required="required"
        :required-text="requiredText"
        :text="label"
        :hidden-label="hiddenLabel"
        :for="inputId"
    >
        <slot name="mds-microcopy-above">
            <mds-microcopy v-if="microcopyAbove" :size="size" :on-dark="onDark" v-html="microcopyAbove" />
        </slot>
        <div v-if="password" :class="$style['mds-input__password-wrapper']">
            <input
                v-bind="$attrs"
                :class="[$style['mds-input'], classObject, sizeClass]"
                v-bind:aria-invalid="error"
                v-on="inputListeners"
                :data-mds-version="version"
                :id="inputId"
                :required="required"
                :type="passwordInputType"
            />
            <mds-button
                :class="$style['mds-input__password-button']"
                type="button"
                variation="icon-only"
                :size="size"
                :icon="showPasswordIcon"
                :on-dark="onDark"
                @click.prevent="isPassword = !isPassword"
                :text="showPasswordText"
            ></mds-button>
        </div>
        <input
            v-else
            v-bind="$attrs"
            :class="[$style['mds-input'], classObject, sizeClass]"
            v-bind:aria-invalid="error"
            v-on="inputListeners"
            :data-mds-version="version"
            :id="inputId"
            :required="required"
        />
        <div v-if="error" :class="$style['mds-input__field-error-wrapper']" aria-live="assertive">
            <mds-field-error
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                :size="size"
                v-html="text"
                :on-dark="onDark"
            />
        </div>
        <slot name="mds-microcopy-below">
            <mds-microcopy v-if="microcopyBelow" :size="size" v-html="microcopyBelow" :on-dark="onDark" />
        </slot>
    </mds-label>
</template>
<script>
    import MdsFieldError from '@mds/field-error';
    import MdsLabel from '@mds/label';
    import MdsMicrocopy from '@mds/microcopy';
    import MdsIcon from '@mds/icon';
    import { MdsButton } from '@mds/button';

    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    MdsIcon.customIcons['eye-crossed'] =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path vector-effect="non-scaling-stroke" d="M6.147 8.079A1.5 1.5 0 017.5 5.931m1.229.645A1.5 1.5 0 017.5 8.931a1.537 1.537 0 01-.3-.031" fill="none" stroke-miterlimit="10"/><path vector-effect="non-scaling-stroke" d="M10.289 4.21c.183.1.357.2.524.314s.319.22.467.336.289.236.422.356A8.707 8.707 0 0113.5 7.5s-2 4-6 4a5.773 5.773 0 01-1.824-.293M4.454 10.644A8.553 8.553 0 011.5 7.5s2-4 6-4a5.762 5.762 0 011.568.215M2.782 12.895l7.511-11.318" fill="none" stroke-miterlimit="10"/></svg>';

    export default {
        name: 'MdsInput',
        inheritAttrs: false,
        components: {
            MdsLabel,
            MdsFieldError,
            MdsMicrocopy,
            MdsButton,
        },
        props: {
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, triggers the error state and shows the `errorText` for the input.',
                },
            },
            errorText: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the error text that appears when `error=true` for the input.',
                    support: ['v-html'],
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, visibly hides the input’s label.',
                },
            },
            label: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the label for the input.',
                },
            },
            microcopyAbove: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the input.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the input.',
                    support: ['v-html'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the input label.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe an input as optional.',
                },
            },
            password: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, renders the password variation of the input by setting `type="password"` and adding a button to hide and show the input value.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the input label.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe an input as required.',
                },
            },
            rightAligned: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, right aligns the input’s text.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the input.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
        },
        data() {
            return {
                isPassword: true,
                labels: {
                    showPassword: 'Show password',
                    hidePassword: 'Hide password',
                },
            };
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-input--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-input--error']] = this.error;
                obj[this.$style['mds-input--right-aligned']] = this.rightAligned;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            inputListeners() {
                var vm = this;
                return Object.assign({}, this.$listeners, {
                    input(event) {
                        vm.$emit('input', event.target.value, event);
                    },
                });
            },
            passwordInputType() {
                return this.isPassword ? 'password' : 'text';
            },
            showPasswordIcon() {
                return this.isPassword ? 'eye' : 'eye-crossed';
            },
            showPasswordText() {
                return this.isPassword ? this.labels.showPassword : this.labels.hidePassword;
            },
        },
        methods: {
            randomId() {
                const randomId = Math.random() * Math.floor(1000000);
                return `mds-input-${Math.round(randomId)}`;
            },
        },
        created() {
            this.inputId = this.$attrs.id ? this.$attrs.id : this.randomId();
        },
    };
</script>
<style lang="scss" module>
    @import './input.scss';
</style>
